import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import React, { Fragment } from 'react';
import { Language, useTranslation } from '../../i18n/LanguageContext';

const options: Array<{
  code: Language;
  flag: string;
  labelKey: 'language.portuguese' | 'language.english' | 'language.spanish';
}> = [
  { code: 'pt', flag: '🇵🇹', labelKey: 'language.portuguese' },
  { code: 'es', flag: '🇪🇸', labelKey: 'language.spanish' },
  { code: 'en', flag: '🇬🇧', labelKey: 'language.english' },
];

export default function LanguageSelector({
  dark = false,
  compact = false,
  placement = 'bottom',
}: {
  dark?: boolean;
  compact?: boolean;
  placement?: 'top' | 'bottom';
}) {
  const { language, setLanguage, t } = useTranslation();
  const selectedLanguage = options.find((option) => option.code === language) || options[0];

  return (
    <Menu as="div" className="relative inline-flex">
      <Menu.Button
        type="button"
        className={`inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl border px-3 text-sm font-bold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 ${
          dark
            ? 'border-white/15 bg-white/10 text-white hover:bg-white/15'
            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
        }`}
        aria-label={t('language.label')}
      >
        <span className="text-base leading-none" aria-hidden="true">
          {selectedLanguage.flag}
        </span>
        <span>{language.toUpperCase()}</span>
        {!compact && <ChevronDownIcon className="h-3.5 w-3.5" />}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="scale-95 opacity-0"
        enterTo="scale-100 opacity-100"
        leave="transition ease-in duration-75"
        leaveFrom="scale-100 opacity-100"
        leaveTo="scale-95 opacity-0"
      >
        <Menu.Items
          className={`absolute left-0 z-[100] w-44 rounded-xl border border-gray-100 bg-white p-1.5 shadow-xl focus:outline-none ${
            placement === 'top'
              ? 'bottom-full mb-2 origin-bottom-left'
              : 'top-full mt-2 origin-top-left'
          }`}
        >
          {options.map((option) => (
            <Menu.Item key={option.code}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => setLanguage(option.code)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${
                    active ? 'bg-green-cl/5 text-green-cl' : 'text-gray-700'
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <span className="text-base leading-none" aria-hidden="true">{option.flag}</span>
                    <span>{t(option.labelKey)}</span>
                  </span>
                  {language === option.code && <CheckIcon className="h-4 w-4 text-green-cl" />}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
